import { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { sectionPath, translatePath } from "../utils/routePaths";
import { resolveSlug } from "../utils/slugify";
import { useNavOrder } from "../hooks/useNavOrder";

const ACCENT = "#C47C2B";

function SearchResultItem({ item, language, onSelect }) {
  const name = language === "ka" && item.nameKa ? item.nameKa : item.name;
  const img = item.image || "";
  const typeConfig = {
    place: {
      icon: "landscape",
      label: language === "ka" ? "ადგილი" : "Place",
      path: sectionPath(language, "place", resolveSlug(item, language)),
    },
    adventure: {
      icon: "hiking",
      label: language === "ka" ? "თავგადასავალი" : "Adventure",
      path: sectionPath(language, "adventures", resolveSlug(item, language)),
    },
    route: {
      icon: "route",
      label: language === "ka" ? "მარშრუტი" : "Route",
      path: sectionPath(language, ["adventures", "route"], resolveSlug(item, language)),
    },
    culture: {
      icon: "auto_stories",
      label: language === "ka" ? "კულტურა" : "Culture",
      path: sectionPath(language, ["culture", "article"], resolveSlug(item, language)),
    },
    cuisine: {
      icon: "restaurant",
      label: language === "ka" ? "კერძი" : "Cuisine",
      path: sectionPath(language, "cuisine", resolveSlug(item, language)),
    },
    festival: {
      icon: "celebration",
      label: language === "ka" ? "დღესასწაული" : "Festival",
      path: sectionPath(language, "festivals", resolveSlug(item, language)),
    },
    guide: {
      icon: "map",
      label: language === "ka" ? "გზამკვლევი" : "Guide",
      path: sectionPath(language, "guide", resolveSlug(item, language)),
    },
    photo: {
      icon: "photo_camera",
      label: language === "ka" ? "ფოტო" : "Photo Guide",
      path: sectionPath(language, "photo-guide", resolveSlug(item, language)),
    },
    localGuide: {
      icon: "groups",
      label: language === "ka" ? "ადგილობრივი გიდი" : "Local Guide",
      path: sectionPath(language, "local-guides", resolveSlug(item, language)),
    },
    hotel: {
      icon: "apartment",
      label: language === "ka" ? "სასტუმრო" : "Hotel",
      path: sectionPath(language, "hotels", resolveSlug(item, language)),
    },
  }[item._type] || { icon: "search", label: "", path: "/" };

  return (
    <button
      type="button"
      onClick={() => onSelect(typeConfig.path)}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 20px",
        border: "none",
        borderBottom: "1px solid var(--border-soft)",
        background: "transparent",
        cursor: "pointer",
        textAlign: "left",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--bg-surface-alt)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <div
        style={{
          width: 48,
          height: 36,
          borderRadius: 8,
          background: "var(--bg-surface-alt)",
          overflow: "hidden",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {img ? (
          <img
            src={img}
            alt={name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span
            className="material-symbols-outlined"
            style={{ color: "var(--text-muted)", fontSize: 18 }}
          >
            {typeConfig.icon}
          </span>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "Geist, sans-serif",
            fontSize: 15,
            fontWeight: 600,
            color: "var(--text-primary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "var(--text-muted)",
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginTop: 2,
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 12 }}>
            {typeConfig.icon}
          </span>
          {typeConfig.label}
        </div>
      </div>
    </button>
  );
}

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchPlaces, setSearchPlaces] = useState([]);
  const settings = useSiteSettings();
  const { language, switchLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  const { order: navOrder, childOrder: navChildOrder } = useNavOrder();


  const handleLanguageSwitch = (lang) => {
    switchLanguage(lang);
    const newPath = translatePath(location.pathname, lang) + location.search;
    window.history.replaceState(null, "", newPath);
  };

  useEffect(() => {
    const loadAll = async () => {
      try {
        const [p, a, r, c, cu, f, g, ph, lg, h] = await Promise.all([
          getDocs(collection(db, "places")),
          getDocs(collection(db, "adventureCategories")),
          getDocs(collection(db, "adventureRoutes")),
          getDocs(collection(db, "cultureArticles")),
          getDocs(collection(db, "cuisineDishes")),
          getDocs(collection(db, "festivals")),
          getDocs(collection(db, "travelGuides")),
          getDocs(collection(db, "photoGuideCategories")),
          getDocs(collection(db, "localGuides")),
          getDocs(collection(db, "hotels")),
        ]);
        setSearchPlaces([
          ...p.docs.map((d) => ({ id: d.id, _type: "place", ...d.data() })),
          ...a.docs.map((d) => ({ id: d.id, _type: "adventure", ...d.data() })),
          ...r.docs.map((d) => ({ id: d.id, _type: "route", ...d.data() })),
          ...c.docs.map((d) => ({ id: d.id, _type: "culture", ...d.data() })),
          ...cu.docs.map((d) => ({ id: d.id, _type: "cuisine", ...d.data() })),
          ...f.docs.map((d) => ({ id: d.id, _type: "festival", ...d.data() })),
          ...g.docs.map((d) => ({ id: d.id, _type: "guide", ...d.data() })),
          ...ph.docs.map((d) => ({ id: d.id, _type: "photo", ...d.data() })),
          ...lg.docs.map((d) => {
            const data = d.data();
            return { id: d.id, _type: "localGuide", ...data, image: data.photo };
          }),
          ...h.docs
            .map((d) => ({ id: d.id, _type: "hotel", ...d.data() }))
            .filter((hotel) => hotel.active !== false),
        ]);
      } catch (err) {
        console.error(err);
      }
    };
    loadAll();
  }, []);

  const flattenLegends = (legends) =>
    (legends || []).map((l) => (typeof l === "object" ? l?.text : l)).filter(Boolean);

  const getPrimaryText = (item) =>
    [item.name, item.nameKa, item.title, item.category, item.location]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const getSecondaryText = (item) =>
    [
      item.description,
      item.descriptionKa,
      item.history,
      item.historyKa,
      item.traditions,
      item.traditionsKa,
      item.month,
      item.monthKa,
      item.longDescription,
      item.longDescriptionKa,
      item.legend,
      ...flattenLegends(item.legends),
      ...flattenLegends(item.legendsKa),
      ...(item.interestingFacts || []),
      ...(item.interestingFactsKa || []),
      item.elevation,
      item.difficulty,
      item.bestSeason,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const searchResults = (() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return [];
    const primaryMatches = searchPlaces.filter((p) =>
      getPrimaryText(p).includes(term),
    );
    if (primaryMatches.length >= 6) return primaryMatches.slice(0, 6);
    const secondaryMatches = searchPlaces.filter(
      (p) =>
        !getPrimaryText(p).includes(term) &&
        getSecondaryText(p).includes(term),
    );
    return [...primaryMatches, ...secondaryMatches].slice(0, 6);
  })();

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setActiveDropdown(null);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const openDropdown = (key) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveDropdown(key);
  };
  const closeDropdown = () => {
    timeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  };
  const keepOpen = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const navGroups = [
    {
      key: "explore",
      label: t("nav_explore") || "Explore",
      path: "/" + language,
      single: true,
    },
    {
      key: "discover",
      label: language === "ka" ? "აღმოაჩინე" : "Discover",
      items: [
        {
          id: "categories",
          label: t("nav_categories") || "Categories",
          path: sectionPath(language, "categories"),
          icon: "grid_view",
          desc:
            language === "ka" ? "ადგილები კატეგორიებით" : "Places by category",
        },
        {
          id: "adventures",
          label: t("nav_adventures") || "Adventures",
          path: sectionPath(language, "adventures"),
          icon: "hiking",
          desc:
            language === "ka"
              ? "ლაშქრობა, ალპინიზმი"
              : "Hiking, climbing, skiing",
        },
        {
          id: "culture",
          label: t("nav_culture") || "Culture",
          path: sectionPath(language, "culture"),
          icon: "auto_stories",
          desc:
            language === "ka"
              ? "ტრადიციები და მემკვიდრეობა"
              : "Traditions & heritage",
        },
        {
          id: "photo-guide",
          label: t("nav_photo_guide") || "Photo Guide",
          path: sectionPath(language, "photo-guide"),
          icon: "photo_camera",
          desc: language === "ka" ? "ფოტო ლოკაციები" : "Photography locations",
        },
      ],
    },
    {
      key: "plan",
      label: language === "ka" ? "დაგეგმე" : "Plan",
      items: [
        {
          id: "hotels",
          label: t("nav_hotels") || "Hotels",
          path: sectionPath(language, "hotels"),
          icon: "apartment",
          desc:
            language === "ka" ? "დაათვალიერე სასტუმროები" : "Browse hotels & guesthouses",
        },
        {
          id: "local-guides",
          label: t("nav_local_guides") || "Local Guides",
          path: sectionPath(language, "local-guides"),
          icon: "groups",
          desc:
            language === "ka" ? "დაჯავშნე ადგილობრივი გიდი" : "Book a local guide",
        },
        {
          id: "guide",
          label: t("nav_guide") || "Travel Guide",
          path: sectionPath(language, "guide"),
          icon: "map",
          desc:
            language === "ka" ? "სრული გზამკვლევი" : "Complete travel guide",
        },
        {
          id: "cuisine",
          label: t("nav_cuisine") || "Svan Cuisine",
          path: sectionPath(language, "cuisine"),
          icon: "restaurant",
          desc: language === "ka" ? "ტრადიციული კერძები" : "Traditional dishes",
        },
        {
          id: "festivals",
          label: t("nav_festivals") || "Festivals",
          path: sectionPath(language, "festivals"),
          icon: "celebration",
          desc:
            language === "ka" ? "დღესასწაულები" : "Traditional celebrations",
        },
      ],
    },
    {
      key: "about",
      label: t("nav_about") || "About",
      path: sectionPath(language, "about"),
      single: true,
    },
  ];
  const orderedNavGroups = navOrder
    .map((key) => navGroups.find((g) => g.key === key))
    .filter(Boolean)
    .map((group) => {
      if (!group.items) return group;
      const childIds = navChildOrder[group.key];
      if (!childIds || !childIds.length) return group;
      const orderedItems = childIds
        .map((id) => group.items.find((it) => it.id === id))
        .filter(Boolean);
      return { ...group, items: orderedItems };
    });
  const isTransparent = false;
  const navBg = "var(--bg-surface)";
  const textColor = "var(--text-primary)";
  const logoColor = "var(--text-primary)";
  const borderBottom = "1px solid var(--border)";

  return (
    <>
      <header
        ref={dropdownRef}
        style={{
          position: "sticky",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: navBg,
          borderBottom: borderBottom,
          boxShadow: isTransparent ? "none" : "0 1px 12px rgba(0,0,0,0.06)",
          transition: "background 0.3s, box-shadow 0.3s",
        }}
      >
        {/* Main bar */}
        <div
          style={{
            padding: "0 60px",
            height: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          className="nav-inner"
        >
          <Link
            to={"/" + language}
            onClick={() => {
              if (location.pathname === "/" + language) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              textDecoration: "none",
              flexShrink: 0,
            }}
          >
            <svg width="32" height="32" viewBox="0 0 28 28" fill="none">
              <path
                d="M2 22 L10 8 L14 14 L18 10 L26 22 Z"
                stroke={logoColor}
                strokeWidth="1.8"
                strokeLinejoin="round"
                fill="none"
              />
              <path
                d="M18 10 L22 6 L26 10"
                stroke={logoColor}
                strokeWidth="1.8"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
            <span
              style={{
                fontFamily: "Geist, sans-serif",
                fontSize: 20,
                fontWeight: 700,
                color: logoColor,
                letterSpacing: "-0.01em",
              }}
            >
              {settings.siteName || "DiscoverSvaneti"}
            </span>
          </Link>

          <nav
            style={{ display: "flex", alignItems: "center", gap: 4 }}
            className="desktop-nav"
          >
            {orderedNavGroups.map((group) => {
              if (group.single) {
                const active = location.pathname === group.path;
                return (
                  <Link
                    key={group.key}
                    to={group.path}
                    style={{
                      fontFamily: "Geist, sans-serif",
                      fontSize: 16,
                      fontWeight: 500,
                      color: active
                        ? isTransparent
                          ? "#fff"
                          : "var(--text-primary)"
                        : textColor,
                      textDecoration: "none",
                      padding: "6px 16px",
                      borderRadius: 6,
                    }}
                  >
                    {group.label}
                  </Link>
                );
              }
              const isOpen = activeDropdown === group.key;
              const isActive =
                group.items &&
                group.items.some((item) => location.pathname === item.path);
              return (
                <div
                  key={group.key}
                  style={{ position: "relative" }}
                  onMouseEnter={() => openDropdown(group.key)}
                  onMouseLeave={closeDropdown}
                >
                  <button
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontFamily: "Geist, sans-serif",
                      fontSize: 16,
                      fontWeight: 500,
                      color:
                        isActive || isOpen
                          ? isTransparent
                            ? "#fff"
                            : "var(--text-primary)"
                          : textColor,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "6px 16px",
                      borderRadius: 6,
                    }}
                  >
                    {group.label}
                    <span
                      className="material-symbols-outlined"
                      style={{
                        fontSize: 16,
                        transform: isOpen ? "rotate(180deg)" : "rotate(0)",
                        transition: "transform 0.2s",
                      }}
                    >
                      expand_more
                    </span>
                  </button>
                  {isOpen && (
                    <div
                      onMouseEnter={keepOpen}
                      onMouseLeave={closeDropdown}
                      style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "var(--bg-surface)",
                        border: "1px solid var(--border)",
                        borderRadius: 16,
                        padding: 8,
                        minWidth: 280,
                        boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
                        zIndex: 100,
                      }}
                    >
                      {group.items.map((item) => {
                        const itemActive = location.pathname === item.path;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setActiveDropdown(null)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              padding: "12px 14px",
                              borderRadius: 10,
                              textDecoration: "none",
                              background: itemActive
                                ? "var(--bg-surface-alt)"
                                : "transparent",
                            }}
                            onMouseEnter={(e) => {
                              if (!itemActive)
                                e.currentTarget.style.background = "var(--bg-surface-alt)";
                            }}
                            onMouseLeave={(e) => {
                              if (!itemActive)
                                e.currentTarget.style.background =
                                  "transparent";
                            }}
                          >
                            <div
                              style={{
                                width: 38,
                                height: 38,
                                borderRadius: 10,
                                background: itemActive
                                  ? ACCENT + "20"
                                  : "var(--bg-surface-alt)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <span
                                className="material-symbols-outlined"
                                style={{
                                  fontSize: 19,
                                  color: itemActive ? ACCENT : "var(--text-secondary)",
                                  fontVariationSettings: "'FILL' 1",
                                }}
                              >
                                {item.icon}
                              </span>
                            </div>
                            <div>
                              <div
                                style={{
                                  fontFamily: "Geist, sans-serif",
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: "var(--text-primary)",
                                }}
                              >
                                {item.label}
                              </div>
                              <div
                                style={{
                                  fontFamily: "Geist, sans-serif",
                                  fontSize: 12,
                                  color: "var(--text-muted)",
                                  marginTop: 2,
                                }}
                              >
                                {item.desc}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={() => setSearchOpen(true)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: textColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 22 }}
              >
                search
              </span>
            </button>
            <button
              onClick={toggleTheme}
              aria-label="Toggle dark mode"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: textColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 22 }}
              >
                {theme === "dark" ? "light_mode" : "dark_mode"}
              </span>
            </button>
            <div
              style={{ display: "flex", alignItems: "center" }}
              className="lang-switcher"
            >
              {["en", "ka"].map((lang) => (
                <button
                  key={lang}
                  onClick={() => handleLanguageSwitch(lang)}
                  style={{
                    background:
                      language === lang ? "var(--bg-inverse)" : "transparent",
                    color:
                      language === lang ? "var(--fg-inverse)" : textColor,
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 14px",
                    fontFamily: "Geist, sans-serif",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    textTransform: "uppercase",
                  }}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="mobile-menu-btn"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: isTransparent ? "#fff" : "var(--text-primary)",
                display: "none",
                alignItems: "center",
                padding: 4,
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 26 }}
              >
                {menuOpen ? "close" : "menu"}
              </span>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div
            style={{
              background: "var(--bg-surface)",
              borderTop: "1px solid var(--border-soft)",
              padding: "8px 20px 20px",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            {orderedNavGroups.map((group) => {
              if (group.single) {
                return (
                  <Link
                    key={group.key}
                    to={group.path}
                    onClick={() => setMenuOpen(false)}
                    style={{
                      display: "block",
                      fontFamily: "Geist, sans-serif",
                      fontSize: 15,
                      fontWeight: 600,
                      color: "var(--text-primary)",
                      textDecoration: "none",
                      padding: "12px 0",
                      borderBottom: "1px solid var(--border-soft)",
                    }}
                  >
                    {group.label}
                  </Link>
                );
              }
              return (
                <div key={group.key}>
                  <div
                    style={{
                      fontFamily: "Geist, sans-serif",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "var(--text-muted)",
                      padding: "12px 0 4px",
                    }}
                  >
                    {group.label}
                  </div>
                  {group.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMenuOpen(false)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        fontFamily: "Geist, sans-serif",
                        fontSize: 14,
                        fontWeight: 500,
                        color: "var(--text-primary)",
                        textDecoration: "none",
                        padding: "10px 0",
                        borderBottom: "1px solid var(--border-soft)",
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{
                          fontSize: 17,
                          color: ACCENT,
                          fontVariationSettings: "'FILL' 1",
                        }}
                      >
                        {item.icon}
                      </span>
                      {item.label}
                    </Link>
                  ))}
                </div>
              );
            })}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              {["en", "ka"].map((lang) => (
                <button
                  key={lang}
                  onClick={() => {
                    handleLanguageSwitch(lang);
                    setMenuOpen(false);
                  }}
                  style={{
                    flex: 1,
                    padding: "10px 0",
                    borderRadius: 8,
                    border: "1.5px solid",
                    borderColor:
                      language === lang ? "var(--bg-inverse)" : "var(--border)",
                    background:
                      language === lang ? "var(--bg-inverse)" : "transparent",
                    color:
                      language === lang ? "var(--fg-inverse)" : "var(--text-primary)",
                    fontFamily: "Geist, sans-serif",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    textTransform: "uppercase",
                  }}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            background: "rgba(26,26,24,0.7)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: 80,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSearchOpen(false);
              setSearchText("");
            }
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 600,
              margin: "0 20px",
              background: "var(--bg-surface)",
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
            }}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSearchOpen(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 20px",
                borderBottom: "1px solid var(--border-soft)",
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ color: "var(--text-muted)", fontSize: 20 }}
              >
                search
              </span>
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                autoFocus
                placeholder={
                  t("search_placeholder") ||
                  "Search places, culture, cuisine..."
                }
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  fontFamily: "Geist, sans-serif",
                  fontSize: 16,
                  color: "var(--text-primary)",
                  background: "transparent",
                }}
              />
              <button
                type="button"
                onClick={() => {
                  setSearchOpen(false);
                  setSearchText("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--text-muted)",
                  display: "flex",
                }}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </form>
            {searchText.trim() && (
              <div>
                {searchResults.length > 0 ? (
                  searchResults.map((item) => (
                    <SearchResultItem
                      key={item._type + "-" + item.id}
                      item={item}
                      language={language}
                      onSelect={(path) => {
                        navigate(path);
                        setSearchOpen(false);
                        setSearchText("");
                      }}
                    />
                  ))
                ) : (
                  <div
                    style={{
                      padding: 24,
                      color: "var(--text-muted)",
                      fontSize: 14,
                      textAlign: "center",
                      fontFamily: "Geist, sans-serif",
                    }}
                  >
                    {t("search_no_results") || "No results found."}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes dropIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .lang-switcher { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
        }
        @media (min-width: 901px) {
          .mobile-menu-btn { display: none !important; }
        }
        @media (max-width: 600px) {
          .nav-inner { padding: 0 20px !important; }
        }
      `}</style>
    </>
  );
}
